import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { ASSET } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Gavel, Clock, TrendingUp, Lock, ArrowLeft, Loader2, Crown, Users } from "lucide-react";

const eur = (n) => `€${Number(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`;

// Returns {d,h,m,s,ended}
function diffParts(target) {
  const t = new Date(target).getTime() - Date.now();
  if (t <= 0) return { d: 0, h: 0, m: 0, s: 0, ended: true };
  const d = Math.floor(t / 86400000);
  const h = Math.floor((t % 86400000) / 3600000);
  const m = Math.floor((t % 3600000) / 60000);
  const s = Math.floor((t % 60000) / 1000);
  return { d, h, m, s, ended: false };
}

function Countdown({ endsAt }) {
  const [parts, setParts] = useState(diffParts(endsAt));
  useEffect(() => {
    const t = setInterval(() => setParts(diffParts(endsAt)), 1000);
    return () => clearInterval(t);
  }, [endsAt]);
  if (parts.ended) {
    return <div data-testid="countdown-ended" className="text-2xl font-bold text-red-400 tracking-wider">ASTA CHIUSA</div>;
  }
  const Cell = ({ v, l }) => (
    <div className="flex flex-col items-center">
      <div className="text-3xl md:text-5xl font-bold tabular-nums text-[#C9A961]">{String(v).padStart(2, "0")}</div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mt-1">{l}</div>
    </div>
  );
  return (
    <div data-testid="countdown" className="flex items-end gap-4 md:gap-6">
      <Cell v={parts.d} l="giorni" />
      <div className="text-3xl md:text-5xl text-white/20 pb-1">:</div>
      <Cell v={parts.h} l="ore" />
      <div className="text-3xl md:text-5xl text-white/20 pb-1">:</div>
      <Cell v={parts.m} l="min" />
      <div className="text-3xl md:text-5xl text-white/20 pb-1">:</div>
      <Cell v={parts.s} l="sec" />
    </div>
  );
}

export default function ClubAuctionDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [auction, setAuction] = useState(null);
  const [bids, setBids] = useState([]);
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const reload = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        api.get(`/club/auctions/${id}`),
        api.get(`/club/auctions/${id}/bids`),
      ]);
      setAuction(a.data);
      setBids(b.data || []);
      if (!amount) setAmount(String(a.data.min_next_bid || a.data.starting_bid));
    } catch (e) {
      setError(e.response?.status === 404 ? "Asta non trovata" : "Errore caricamento");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    reload();
    pollRef.current = setInterval(reload, 5000); // poll every 5s
    return () => clearInterval(pollRef.current);
  }, [reload]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!user) {
      toast.error("Accedi per piazzare un'offerta");
      return;
    }
    const n = Number(amount);
    if (!n || n < auction.min_next_bid) {
      toast.error(`L'offerta minima è ${eur(auction.min_next_bid)}`);
      return;
    }
    setLoading(true);
    try {
      const r = await api.post(`/club/auctions/${id}/bid`, { amount: n });
      toast.success(`Offerta accettata · ${eur(n)} ✓`);
      setAuction(r.data.auction);
      setAmount(String(r.data.auction.min_next_bid));
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Offerta non accettata");
      reload();
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-white">
        <div className="text-center">
          <div className="text-2xl mb-3">{error}</div>
          <Link to="/club#auctions" className="text-[#C9A961] underline">← Torna al Club</Link>
        </div>
      </div>
    );
  }
  if (!auction) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#C9A961]" /></div>;
  }

  const isWinning = auction.current_bid_email === user?.email;
  const ended = auction.ended;

  return (
    <div data-testid="auction-detail-page" className="min-h-screen bg-[#0a0a0a] text-white">
      {/* HERO */}
      <div className="border-b border-white/10 bg-[#070707]">
        <div className="container-xl py-10 md:py-14">
          <Link to="/club#auctions" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-white/50 hover:text-[#C9A961] mb-8" data-testid="back-to-club">
            <ArrowLeft className="h-3.5 w-3.5" /> Torna alle aste
          </Link>
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div className="bg-white/[0.03] border border-white/10 aspect-square overflow-hidden">
              <img src={ASSET(auction.image)} alt={auction.lot_name} className="w-full h-full object-contain p-8" />
            </div>
            <div>
              <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.4em] text-[#C9A961] mb-4">
                <Gavel className="h-3.5 w-3.5" /> Asta privata · {auction.tier_required}
              </div>
              <h1 className="editorial-heading text-3xl md:text-5xl leading-[1.05]">{auction.lot_name}</h1>
              <div className="text-sm uppercase tracking-[0.2em] text-white/50 mt-3">
                {auction.producer} · {auction.format} · {auction.bottles} {auction.bottles > 1 ? "bottiglie" : "bottiglia"}
              </div>
              <p className="text-white/70 mt-6 leading-relaxed">{auction.description}</p>
              <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                <div className="border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Stima</div>
                  <div className="text-lg font-semibold text-[#C9A961]">{eur(auction.estimate_min)} – {eur(auction.estimate_max)}</div>
                </div>
                <div className="border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Provenance</div>
                  <div className="text-sm">{auction.provenance || "—"}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* LIVE BID PANEL */}
      <div className="border-b border-white/10">
        <div className="container-xl py-12 md:py-16">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <div className="text-xs uppercase tracking-[0.4em] text-[#C9A961] mb-3 flex items-center gap-2">
                <Clock className="h-3.5 w-3.5" /> Tempo rimanente
              </div>
              <Countdown endsAt={auction.ends_at} />
            </div>
            <div className="md:border-l md:border-white/10 md:pl-8">
              <div className="text-xs uppercase tracking-[0.4em] text-white/40 mb-2">Offerta corrente</div>
              <div data-testid="current-bid" className="text-4xl md:text-5xl font-bold text-[#C9A961]">
                {eur(auction.current_bid)}
              </div>
              <div className="mt-2 text-xs text-white/50 flex items-center gap-2">
                <Users className="h-3 w-3" />
                <span data-testid="bid-count">{auction.bid_count}</span> {auction.bid_count === 1 ? "offerta" : "offerte"}
                {isWinning && <span className="ml-2 inline-flex items-center gap-1 text-green-400"><Crown className="h-3 w-3" /> Tua</span>}
              </div>
            </div>
          </div>

          {/* BID FORM */}
          <div className="mt-12 max-w-2xl">
            {!user ? (
              <div className="bg-[#7A1C2B]/30 border border-[#7A1C2B] p-6 text-center">
                <Lock className="h-5 w-5 mx-auto mb-2 text-[#C9A961]" />
                <p className="text-sm">
                  <Link to="/login" className="text-[#C9A961] underline">Accedi</Link> al tuo account Club per partecipare all'asta.
                </p>
              </div>
            ) : ended ? (
              <div data-testid="auction-ended-msg" className="bg-black/40 border border-white/10 p-6 text-center text-white/60">
                Quest'asta è chiusa. Il vincitore verrà contattato dal team Concierge entro 24h.
              </div>
            ) : (
              <form onSubmit={submit} data-testid="bid-form" className="bg-black/40 border border-[#C9A961]/30 p-6">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3 flex items-center gap-2">
                  <TrendingUp className="h-3 w-3 text-[#C9A961]" /> Piazza un rilancio (incremento minimo {eur(auction.bid_increment)})
                </div>
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">€</span>
                    <input
                      data-testid="bid-amount-input"
                      type="number"
                      min={auction.min_next_bid}
                      step={auction.bid_increment}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="w-full bg-black/60 border border-white/20 pl-10 pr-4 py-4 text-2xl font-bold tabular-nums text-[#C9A961] focus:border-[#C9A961] outline-none"
                    />
                  </div>
                  <button
                    data-testid="bid-submit"
                    type="submit"
                    disabled={loading}
                    className="px-8 bg-[#C9A961] hover:bg-white text-black font-bold uppercase tracking-[0.2em] text-xs disabled:opacity-50 transition-colors"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Rilancia"}
                  </button>
                </div>
                <div className="mt-3 text-xs text-white/50">
                  Offerta minima accettata: <span className="text-[#C9A961] font-semibold">{eur(auction.min_next_bid)}</span>
                  {" · "}le offerte sono vincolanti.
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* BID HISTORY */}
      <div className="container-xl py-12 md:py-16">
        <div className="text-xs uppercase tracking-[0.4em] text-[#C9A961] mb-6">Storico offerte</div>
        {bids.length === 0 ? (
          <div className="text-white/40 text-sm py-8 text-center">Nessuna offerta ancora — sii il primo a rilanciare.</div>
        ) : (
          <div data-testid="bid-history" className="border border-white/10 divide-y divide-white/5">
            {bids.map((b, i) => (
              <div key={b.id} data-testid={`history-bid-${i}`} className="flex items-center justify-between px-5 py-4 hover:bg-white/[0.02]">
                <div className="flex items-center gap-4">
                  <div className={`text-xs font-bold w-8 ${i === 0 ? "text-[#C9A961]" : "text-white/30"}`}>#{i + 1}</div>
                  <div>
                    <div className="font-mono text-sm text-white/70">{b.bidder}</div>
                    <div className="text-[10px] text-white/40">{new Date(b.created_at).toLocaleString("it-IT")}</div>
                  </div>
                </div>
                <div className={`text-lg font-bold tabular-nums ${i === 0 ? "text-[#C9A961]" : "text-white/60"}`}>{eur(b.amount)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
